import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import { Box, Popover, Typography } from "@mui/material";
import { Suspense, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { UserDto } from "../../api";
import { logout as authLogout } from "../../auth/authService";
import { routes } from "../../constants/routes";
import { ErrorContext } from "../../context/ErrorContext";
import { GoogleServicesContext } from "../../context/GoogleServicesContext";
import { UserContext } from "../../context/UserContext";
import { colors } from "../../theme/colors";
import { gaAction, gaCategory } from "../../utils/analytics";
import Button, { ButtonSize, ButtonVariant } from "./Button";
import NavButton from "./NavButton";

const AccountActions = ({
  user,
  logout,
  handleClose
}: {
  user?: UserDto;
  logout: () => void;
  handleClose?: () => void;
}) => {
  const { t } = useTranslation();
  const { analyticsEvent } = useContext(GoogleServicesContext);

  return user ? (
    <>
      <Typography variant="h5" mb={2} fontWeight="bold" textAlign="center">
        {user.companyName}
      </Typography>

      <NavButton
        onClick={() => {
          handleClose?.();
          analyticsEvent(gaCategory.passwordChange, gaAction.click);
        }}
        route={routes.passwordChange}
        label={t("buttons.changePassword")}
        variant={ButtonVariant.secondary}
        size={ButtonSize.full}
      />

      <Button
        onClick={logout}
        label={t("buttons.logout")}
        variant={ButtonVariant.secondary}
        size={ButtonSize.full}
      />
    </>
  ) : (
    <>
      <NavButton
        onClick={() => {
          handleClose?.();
          analyticsEvent(gaCategory.login, gaAction.click);
        }}
        route={routes.login}
        label={t("buttons.login")}
        variant={ButtonVariant.secondary}
        size={ButtonSize.full}
      />

      <NavButton
        onClick={() => {
          handleClose?.();
          analyticsEvent(gaCategory.partner, gaAction.click);
        }}
        route={routes.partner}
        label={t("buttons.becomePartner")}
        variant={ButtonVariant.primary}
        size={ButtonSize.full}
      />
    </>
  );
};

export const AccountAvatar = ({
  mobile,
  setMobileNavOpen
}: {
  mobile?: boolean;
  setMobileNavOpen?: () => void;
}) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { user, setUser } = useContext(UserContext);
  const { setError } = useContext(ErrorContext);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const logout = () => {
    handleClose();
    if (mobile && setMobileNavOpen) {
      setMobileNavOpen();
    }

    authLogout().then((success) => {
      if (success) {
        setUser(undefined);
      } else {
        setError("logout");
      }
    });
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <Suspense fallback={<></>}>
      {mobile && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}>
          <AccountActions user={user} logout={logout} handleClose={setMobileNavOpen} />
        </Box>
      )}
      {!mobile && (
        <>
          <Button
            onClick={handleOpen}
            startIcon={
              user ? (
                <AccountCircleIcon titleAccess={user.companyName} />
              ) : (
                <AccountCircleOutlinedIcon
                  titleAccess={`${t("buttons.login")}, ${t("buttons.becomePartner")}`}
                />
              )
            }
            variant={ButtonVariant.accent}
            sx={
              [routes.login, routes.passwordChange, routes.passwordReset, routes.partner].includes(
                location.pathname
              )
                ? {
                    color: colors.green,
                    backgroundColor: "transparent",
                    border: `3px solid ${colors.green}`
                  }
                : undefined
            }
          />
          <Popover
            id={id}
            open={open}
            onClose={handleClose}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right"
            }}
            sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                justifyContent: "center"
              }}>
              <AccountActions user={user} logout={logout} handleClose={handleClose} />
            </Box>
          </Popover>
        </>
      )}
    </Suspense>
  );
};
